import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FaArrowLeft } from 'react-icons/fa'
import { billingHistoryApi } from '../../../apis/payments.api'
import axios from '../../../apis/axiosConfig'
import { useDarkMode } from '../../../hooks/useDarkMode'
import { useApiErrorHandler } from '../../../hooks/useApiErrorHandler'
    import { Helmet } from "react-helmet";

export default function BillingHistory() {
  const { isDarkMode } = useDarkMode()
  const navigate = useNavigate()
  const handleApiError = useApiErrorHandler()
  const [records, setRecords] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setLoading(true)
        const res = await billingHistoryApi()
        const list = res.data?.billing_history || []
        setRecords(list)
        setError(null)
      } catch (err) {
        // Check if it's an auth error - handleApiError will handle redirect
        const isAuthError = handleApiError(err)
        if (!isAuthError) {
          setError(err.message || 'Failed to load billing history')
        }
      } finally {
        setLoading(false)
      }
    }
    fetchHistory()
  }, [handleApiError])

  const downloadInvoice = async (invoiceId) => {
    try {
      const res = await axios.get(`download-invoice/${invoiceId}/`, {
        responseType: 'blob'
      })

      const blob = new Blob([res.data])
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${invoiceId}.pdf`
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      const isAuthError = handleApiError(err)
      if (!isAuthError) {
        const status = err.response?.status
        if (status === 404) {
          alert('Invoice not found')
        } else {
          alert('Failed to download invoice')
        }
      }
    }
  }

  return (
    <>


<Helmet>

  {/* Title */}
  <title>Billing History | Seo Up Rank Dashboard</title>

  {/* Meta Description */}
  <meta
    name="description"
    content="View your billing history, invoices, and payment details in the Seo Up Rank dashboard."
  />

  {/* Private Page */}
  <meta name="robots" content="noindex, nofollow" />

  {/* Canonical */}
  <link rel="canonical" href="https://novarsis.ai/billing-history" />

  {/* Open Graph */}
  <meta property="og:title" content="Seo Up Rank Billing History" />
  <meta
    property="og:description"
    content="Access your Seo Up Rank billing history and payment records from your dashboard."
  />
  <meta property="og:type" content="website" />
  <meta property="og:url" content="https://novarsis.ai/billing-history" />
  <meta property="og:site_name" content="Seo Up Rank" />

  {/* Twitter */}
  <meta name="twitter:card" content="summary" />
  <meta name="twitter:title" content="Seo Up Rank Billing History" />
  <meta
    name="twitter:description"
    content="Check your Seo Up Rank billing history and invoices."
  />

  {/* Schema */}
  <script type="application/ld+json">
    {`
    {
      "@context":"https://schema.org",
      "@type":"WebPage",
      "name":"Billing History - Seo Up Rank",
      "url":"https://novarsis.ai/billing-history",
      "description":"User billing history page for viewing invoices and payment records in Seo Up Rank dashboard."
    }
    `}
  </script>

</Helmet>
   
    <div className={`p-3 sm:p-4 md:p-6 w-[95%] sm:w-4/5 mx-auto rounded-lg sm:rounded-2xl shadow my-6 sm:my-10 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
      {/* Header */}
      <div className="flex items-center gap-3 sm:gap-4 mb-6">
        <button
          onClick={() => navigate(-1)}
          className={`p-2 rounded-lg transition-colors duration-200 shadow-sm flex-shrink-0 ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-white'}`}
        >
          <FaArrowLeft className={`text-lg sm:text-xl ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} />
        </button>
        <h1 className={`text-2xl sm:text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Billing History</h1>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className={`flex items-center justify-center h-48 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Loading...</div>
      ) : error ? (
        <div className={`px-4 py-3 rounded text-sm sm:text-base ${isDarkMode ? 'bg-red-900 border border-red-700 text-red-200' : 'bg-red-50 border border-red-200 text-red-700'}`}>{error}</div>
      ) : records.length === 0 ? (
        <div className={`rounded-lg shadow p-6 text-center text-sm sm:text-base ${isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-white text-gray-600'}`}>No billing history yet.</div>
      ) : (
        <>
          {/* Mobile View - Card Layout */}
          <div className="lg:hidden space-y-4">
            {records.map((r) => (
              <div
                key={r.payment_id}
                className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}
              >
                {/* Payment ID */}
                <div className="mb-3 pb-3 border-b border-gray-600">
                  <p className={`text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Payment ID</p>
                  <p className={`text-sm font-mono ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`} title={r.payment_id}>
                    {r.payment_id?.slice(0, 20)}...
                  </p>
                </div>

                {/* Invoice ID */}
                <div className="mb-3 pb-3 border-b border-gray-600">
                  <p className={`text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Invoice ID</p>
                  <p className={`text-sm font-mono ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`} title={r.invoice_id}>
                    {r.invoice_id?.slice(0, 20)}...
                  </p>
                </div>

                {/* Services */}
                <div className="mb-3 pb-3 border-b border-gray-600">
                  <p className={`text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Services</p>
                  <p className={`text-sm line-clamp-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>{r.service}</p>
                </div>

                {/* Billing Cycle and Date */}
                <div className="grid grid-cols-2 gap-3 mb-3 pb-3 border-b border-gray-600">
                  <div>
                    <p className={`text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Cycle</p>
                    <p className={`text-sm capitalize ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>{r.billing_cycle || 'N/A'}</p>
                  </div>
                  <div>
                    <p className={`text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Paid At</p>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>{r.paid_at}</p>
                  </div>
                </div>

                {/* Amount */}
                <div className="mb-3 pb-3 border-b border-gray-600">
                  <p className={`text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Amount</p>
                  <p className={`text-lg font-bold ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                    ₹{r.amount_inr ?? r.amount ?? r.total_amount ?? r.price ?? '0'}
                  </p>
                </div>

                {/* Invoice Button */}
                <div className="flex justify-end">
                  {r.invoice_url ? (
                    <a
                      href={r.invoice_url}
                      className="inline-flex items-center px-4 py-2 bg-[#008300] text-white text-sm rounded hover:bg-[#006600] transition-colors"
                      target="_blank"
                      rel="noreferrer"
                    >
                      View Invoice
                    </a>
                  ) : (
                    <button
                      onClick={() => downloadInvoice(r.invoice_id)}
                      className="inline-flex items-center px-4 py-2 bg-[#008300] text-white text-sm rounded hover:bg-[#006600] transition-colors active:bg-[#005500]"
                    >
                      Download
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop View - Table Layout */}
          <div className="hidden lg:block rounded-lg shadow overflow-x-auto">
            <table className={`w-full ${isDarkMode ? 'bg-gray-700' : 'bg-white'}`}>
              <thead className={isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}>
                <tr>
                  <th className={`px-4 lg:px-6 py-3 text-left text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Payment ID</th>
                  <th className={`px-4 lg:px-6 py-3 text-left text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Invoice ID</th>
                  <th className={`px-4 lg:px-6 py-3 text-left text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Services</th>
                  <th className={`px-4 lg:px-6 py-3 text-left text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Cycle</th>
                  <th className={`px-4 lg:px-6 py-3 text-left text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Paid At</th>
                  <th className={`px-4 lg:px-6 py-3 text-right text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Amount</th>
                  <th className={`px-4 lg:px-6 py-3 text-center text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Invoice</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDarkMode ? 'bg-gray-700 divide-gray-600' : 'bg-white divide-gray-200'}`}>
                {records.map((r) => (
                  <tr key={r.payment_id} className={isDarkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-50'}>
                    <td className={`px-4 lg:px-6 py-4 text-sm max-w-[120px] truncate ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} title={r.payment_id}>{r.payment_id}</td>
                    <td className={`px-4 lg:px-6 py-4 text-sm max-w-[110px] truncate ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} title={r.invoice_id}>{r.invoice_id}</td>
                    <td className={`px-4 lg:px-6 py-4 text-sm max-w-[180px] line-clamp-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{r.service}</td>
                    <td className={`px-4 lg:px-6 py-4 text-sm capitalize whitespace-nowrap ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{r.billing_cycle || 'N/A'}</td>
                    <td className={`px-4 lg:px-6 py-4 text-sm whitespace-nowrap ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{r.paid_at}</td>
                    <td className={`px-4 lg:px-6 py-4 text-sm text-right font-semibold whitespace-nowrap ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                      ₹{r.amount_inr ?? r.amount ?? r.total_amount ?? r.price ?? '0'}
                    </td>
                    <td className="px-4 lg:px-6 py-4 text-center whitespace-nowrap">
                      {r.invoice_url ? (
                        <a
                          href={r.invoice_url}
                          className="inline-flex items-center px-3 py-1.5 bg-[#008300] text-white text-sm rounded hover:bg-[#006600] transition-colors"
                          target="_blank"
                          rel="noreferrer"
                        >
                          View
                        </a>
                      ) : (
                        <button
                          onClick={() => downloadInvoice(r.invoice_id)}
                          className="inline-flex items-center px-3 py-1.5 bg-[#008300] text-white text-sm rounded hover:bg-[#006600] transition-colors active:bg-[#005500]"
                        >
                          Download
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div> </>
  )
}